import { Engine } from 'engine/Engine';
import { Entity } from 'engine/Entity';
import { ColliderFacet, ColliderType } from 'engine/facets/ColliderFacet';
import { Facet } from 'engine/facets/Facet';
import { KinematicBodyFacet } from 'engine/facets/KinematicBodyFacet';
import { SpriteFacet } from 'engine/facets/SpriteFacet';
import { SpriteGroupFacet } from 'engine/facets/SpriteGroupFacet';
import { StaticSpriteFacet } from 'engine/facets/StaticSpriteFacet';
import { AudioManager } from 'engine/lib/AudioManager';
import { V2, emptyV2, getDistance, isSameV2, scaleV2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { Activatable } from 'game/behaviors/Activatable';
import { HealthBarBehavior } from 'game/behaviors/HealthBar/HealthBarBehavior';
import { Hittable } from 'game/behaviors/Hittable';
import { Laserable } from 'game/behaviors/Laserable';
import { Waterable } from 'game/behaviors/Waterable';
import { CollisionLayer, concreteMap, grassMap, pointInRect } from 'game/config';
import { nonEmpty } from 'game/lib/NonEmpty';
import { rand } from 'game/lib/rand';
import { PlayerShadow, PlayerSprite } from 'game/sprites/PlayerSprite';
import { PlayerState } from 'game/state/PlayerState';
import random from 'lodash/random';
import { BulletEntity } from './BulletEntity';

const boom = require('game/sfx/boom.mp3');

const boomAudio = new AudioManager(boom);

/* eslint-disable global-require */
const playerHits = nonEmpty([
  new AudioManager(require('game/sfx/player-hit-01.mp3')),
  new AudioManager(require('game/sfx/player-hit-02.mp3')),
  new AudioManager(require('game/sfx/player-hit-03.mp3')),
  new AudioManager(require('game/sfx/player-hit-04.mp3')),
]);

const footsteps = nonEmpty([
  new AudioManager(require('game/sfx/footsteps-01.mp3')),
  new AudioManager(require('game/sfx/footsteps-02.mp3')),
  new AudioManager(require('game/sfx/footsteps-03.mp3')),
  new AudioManager(require('game/sfx/footsteps-04.mp3')),
  new AudioManager(require('game/sfx/footsteps-05.mp3')),
]);

const footstepsConcrete = nonEmpty([
  new AudioManager(require('game/sfx/footsteps-concrete-01.mp3')),
  new AudioManager(require('game/sfx/footsteps-concrete-02.mp3')),
  new AudioManager(require('game/sfx/footsteps-concrete-03.mp3')),
]);
/* eslint-enable global-require */

interface PlayerBehaviorProps {
  playerState: PlayerState,
  activatableCollider: ColliderFacet,
  sprite: SpriteFacet,
  spriteGroup: SpriteGroupFacet,
  onDeath: (engine: Engine) => void,
}

export class PlayerBehavior extends Facet<PlayerBehaviorProps> {

  activated: Activatable | undefined = undefined;

  hitStart = -Infinity;
  hitVelocity: V2 = [0, 0];
  iframeDuration = 600;
  stunDuration = 250;
  lastFrame = -1;

  working = false;

  registerHit = (hitter: Entity, engine: Engine) => {
    if (engine.time.now - this.hitStart <= this.iframeDuration) return false;
    if (hitter instanceof BulletEntity) {
      this.hitStart = engine.time.now;
      this.props.playerState.hp -= hitter.props.damage;
      this.hitVelocity = hitter.props.velocity;
      rand(playerHits).clone().play();
    }
    return true;
  };

  start() {
    this.entity.transform.position = this.props.playerState.position;
  }

  // eslint-disable-next-line complexity
  update(delta: number, engine: Engine) {
    const { input, time } = engine;
    if (!time.timeScale) return;
    const { sprite, playerState } = this.props;
    const deltaHit = time.now - this.hitStart;
    const isIFraming = deltaHit <= this.iframeDuration;
    const isStunned = deltaHit <= this.stunDuration;
    sprite.props.opacity = (isIFraming && !playerState.didDie) ? Number(!sprite.props.opacity) : 1;

    if (isStunned) {
      sprite.set(PlayerSprite.stunned);
      if (playerState.hp <= 0 && !playerState.didDie) {
        playerState.hp = 0;
        playerState.didDie = true;
        boomAudio.play();
        this.props.onDeath(engine);
      }
      return;
    }
    if (playerState.didDie) {
      sprite.set(PlayerSprite.idle);
      return;
    }

    if (this.working) {
      sprite.set(PlayerSprite.working);
      return;
    }

    const { position } = this.entity.transform;
    const axes = input.getAxes();
    if (isSameV2(axes, emptyV2)) {
      this.lastFrame = -1;
      sprite.set(PlayerSprite.idle);
    } else {
      sprite.set(PlayerSprite.running);
      if (sprite.frame !== this.lastFrame && (sprite.frame === 1 || sprite.frame === 3)) {
        this.lastFrame = sprite.frame;
        const onGrass = grassMap.some(rect => pointInRect(position, rect));
        const onConcrete = !onGrass || concreteMap.some(rect => pointInRect(position, rect));
        const sound = onConcrete
          ? rand(footstepsConcrete).clone({ volume: random(0.2, 0.5), playbackRate: random(0.7, 1) })
          : rand(footsteps).clone({ volume: random(0.8, 1), playbackRate: random(0.7, 1) });
        sound.play();
      }
      if (axes[0] > 0) {
        playerState.facing = 'right';
      } else if (axes[0] < 0) {
        playerState.facing = 'left';
      }
    }
    const { facing } = playerState;
    this.props.spriteGroup.transform.scale[0] = facing === 'right' ? 1 : -1;

    const activatables: Activatable[] = [];
    this.props.activatableCollider.collisions.forEach(c => {
      const activatable = c.entity.getFacet(Activatable);
      if (activatable && !activatable.disabled) activatables.push(activatable);
    });
    activatables.sort((a, b) => (
      getDistance(a.entity.transform.position, position) - getDistance(b.entity.transform.position, position)
    ));
    const activatable = activatables[0];

    if (this.activated !== activatable || (this.activated && !this.activated.active)) {
      this.activated?.activate(false, engine);
      this.activated = activatable;
      activatable?.activate(true, engine);
    }
  }

  physicsUpdate(delta: number, engine: Engine) {
    if (this.working) return;
    const body = this.entity.getFacet(KinematicBodyFacet);
    if (!body) return;
    if (engine.time.now - this.hitStart <= this.stunDuration) {
      body.move(scaleV2([...this.hitVelocity], 0.75 * delta), engine);
      return;
    }
    const axes = engine.input.getAxes();
    body.move(scaleV2(axes, this.props.playerState.speed * delta), engine);
  }
}

interface PlayerEntityProps {
  playerState: PlayerState,
  onDeath: (engine: Engine) => void,
}

export class PlayerEntity extends Entity<PlayerEntityProps> {

  repair = (repairing: boolean) => {
    const behavior = this.getFacet(PlayerBehavior);
    if (!behavior) return;
    behavior.working = repairing;
  };

  createFacets({ playerState, onDeath }: PlayerEntityProps) {
    const activatableCollider = create(
      ColliderFacet,
      { type: ColliderType.Box, offset: [-120, -80], size: [240, 140], trigger: true },
    );
    const hitbox = create(
      ColliderFacet,
      {
        type: ColliderType.Box,
        offset: [-45, -200],
        size: [90, 150],
        trigger: true,
        layer: CollisionLayer.Hitbox,
      },
    );
    const sprite = create(SpriteFacet, { frameset: PlayerSprite.idle, offset: [-80, -230] });
    const spriteGroup = create(SpriteGroupFacet, { children: [sprite] });
    const behavior = create(PlayerBehavior, {
      onDeath,
      playerState,
      activatableCollider,
      sprite,
      spriteGroup,
    });

    return [
      spriteGroup,
      create(StaticSpriteFacet, { frame: PlayerShadow }),
      create(KinematicBodyFacet, {}),
      behavior,
      activatableCollider,
      hitbox,
      create(ColliderFacet, { type: ColliderType.Box, size: [50, 20], offset: [-25, -20] }),
      create(ColliderFacet, { type: ColliderType.Circle, radius: 12, offset: [15, -22] }),
      create(ColliderFacet, { type: ColliderType.Circle, radius: 12, offset: [-35, -22] }),
      create(Hittable, { hit: behavior.registerHit }),
      create(Waterable, {
        maxDistance: 50,
        offset: () => emptyV2,
        amountNeeded: () => 0.1,
        water: () => false,
      }),
      create(Laserable, { didSucceed: () => playerState.didDie }),
      create(HealthBarBehavior, {
        getHp: () => playerState.hp,
        maxHp: 100,
        width: 120,
        offset: [0, -270],
      }),
    ];
  }
}
